import React, { useEffect, useState, useMemo } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Popconfirm,
  Space,
  Pagination,
  TableProps,
  Tabs,
} from 'antd';
import { createStyles } from 'antd-style';
import { useSearchParams } from 'react-router-dom';
import { HomeOutlined, EyeOutlined } from '@ant-design/icons';
import { theme, Modal, Descriptions } from 'antd';
import { showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

import CopyButton from '../../../../components/copyButton';
import TimberInventory from './TimberInventory';
import PlyAndMdfInventory, { isPlyAndMdfCategory } from './PlyAndMdfInventory';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Products {
  product_id: number;
  product_name: string;
  product_code: string;
  ut_name: string;
  product_qty: string;
  category_name: string;
  brand_name: string;
  product_price: number;
  product_status: number;
  total_issued_qty?: number;
}

const InventoryManagements: React.FC = () => {
  const [products, setProducts] = useState<Products[]>([]);
  const [form] = Form.useForm();
  const [selectedProduct, setSelectedProduct] = useState<Products | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [issuedEmployees, setIssuedEmployees] = useState<any[]>([]);
  const [loadingIssued, setLoadingIssued] = useState(false);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const typeParam = searchParams.get('type');

  // Auto-resolve active tab from tabParam or sidebar's typeParam
  const activeTab = useMemo(() => {
    if (tabParam) return tabParam;
    if (typeParam === 'timber') return 'timber';
    if (typeParam === 'ply-and-mdf' || typeParam === 'ply_and_mdf' || typeParam === 'ply') return 'ply-and-mdf';
    return 'products';
  }, [tabParam, typeParam]);

  // Safe category_type filter for regular store products (never pass timber or ply-and-mdf to products API)
  const categoryType = useMemo(() => {
    if (
      typeParam &&
      typeParam !== 'timber' &&
      typeParam !== 'ply-and-mdf' &&
      typeParam !== 'ply_and_mdf' &&
      typeParam !== 'undefined' &&
      typeParam !== 'null'
    ) {
      return typeParam;
    }
    return null;
  }, [typeParam]);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchProduct = async (
    page = 1,
    limit = 10
  ) => {
    try {
      let url = `get-handicraft-products?page=${page}&limit=${limit}`;
      if (categoryType) {
        url += `&category_type=${encodeURIComponent(categoryType)}`;
      }

      const response = await apiRequest<{
        data: Products[];
        page: number;
        limit: number;
        total: number;
      }>({
        method: 'get',
        url,
      });
      setProducts(response.data || []);
      setPagination({
        current: response.page || page,
        pageSize: response.limit || limit,
        total: response.total || 0,
      });
    } catch {
      showError('Failed to fetch inventory products');
    }
  };

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProduct(pagination.current, pagination.pageSize);
    }
  }, [searchParams, activeTab, categoryType]);

  const handleTableChange: TableProps<Products>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  // Show products other than ply-and-mdf
  const storeProducts = useMemo(() => {
    return products.filter((p) => !isPlyAndMdfCategory(p.category_name));
  }, [products]);

  const columns: TableColumnsType<Products> = [
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      filters: Array.from(new Set(storeProducts.map((p) => p.product_name)))
        .filter((name): name is string => typeof name === 'string' && name.length > 0)
        .map((name) => ({
          text: name,
          value: name,
        })),
      filterSearch: true,
      filteredValue: filteredInfo.product_name || null,
      onFilter: (value, record) => record.product_name.includes(value as string),
      sorter: (a, b) => a.product_name.localeCompare(b.product_name),
      sortOrder: sortedInfo.columnKey === 'product_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{record.category_name || 'N/A'}</span>
          {record.category_name && <CopyButton textToCopy={record.category_name} />}
        </div>
      ),
    },
    {
      title: 'Brand Name',
      dataIndex: 'brand_name',
      key: 'brand_name',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{record.brand_name || 'N/A'}</span>
          {record.brand_name && <CopyButton textToCopy={record.brand_name} />}
        </div>
      ),
    },
    {
      title: 'Available Qty',
      dataIndex: 'product_qty',
      key: 'product_qty',
      sortOrder: sortedInfo.columnKey === 'product_qty' ? sortedInfo.order : null,
      render: (_, record) => (
        <span style={{ fontWeight: 700, color: '#52c41a' }}>
          {record.product_qty} {record.ut_name}
        </span>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={async () => {
            setSelectedProduct(record);
            setIsViewModalOpen(true);
            setLoadingIssued(true);
            try {
              const res = await apiRequest<any[]>({
                method: "get",
                url: `/get-product-issued-employees?product_id=${record.product_id}`,
              });
              setIssuedEmployees(res || []);
            } catch {
              setIssuedEmployees([]);
            } finally {
              setLoadingIssued(false);
            }
          }}
        >
          View Product
        </Button>
      ),
    },
  ];

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Inventory</Breadcrumb.Item>
        </Breadcrumb>
      </Space>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('tab', key);
          if (key === 'timber') {
            newParams.set('type', 'timber');
          } else if (key === 'ply-and-mdf') {
            newParams.set('type', 'ply-and-mdf');
          } else {
            if (newParams.get('type') === 'timber' || newParams.get('type') === 'ply-and-mdf') {
              newParams.delete('type');
            }
          }
          setSearchParams(newParams);
        }}
        type="card"
        size="large"
        items={[
          {
            key: 'products',
            label: '📦 Product / Store Inventory',
            children: (
              <div
                style={{
                  minHeight: 360,
                  background: colorBgContainer,
                  borderRadius: borderRadiusLG,
                  padding: 16,
                }}
              >
                <Table<Products>
                  tableLayout="auto"
                  className={`${styles.customTable} scrollable-table`}
                  columns={columns}
                  onChange={handleTableChange}
                  rowKey="product_id"
                  dataSource={storeProducts}
                  bordered
                  pagination={false}
                  scroll={{ y: 37 * 15 }}
                  locale={{ emptyText: 'No product found' }}
                  footer={() => (
                    <Flex justify="space-between" align="center">
                      <span>
                        Showing {storeProducts.length === 0 ? 0 : (pagination.current - 1) * pagination.pageSize + 1}–
                        {Math.min(pagination.current * pagination.pageSize, pagination.total || storeProducts.length)} of{' '}
                        {pagination.total || storeProducts.length}
                      </span>
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total || storeProducts.length}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '50', '100']}
                        onShowSizeChange={(_, size) => {
                          setPagination((prev) => ({ ...prev, current: 1, pageSize: size }));
                          fetchProduct(1, size);
                        }}
                        onChange={(page, pageSize) => {
                          setPagination((prev) => ({ ...prev, current: page, pageSize }));
                          fetchProduct(page, pageSize);
                        }}
                      />
                    </Flex>
                  )}
                />
              </div>
            ),
          },
          {
            key: 'timber',
            label: '🪵 Timber Inventory',
            children: <TimberInventory />,
          },
          {
            key: 'ply-and-mdf',
            label: '🪵 Ply & MDF Inventory',
            children: <PlyAndMdfInventory />,
          },
        ]}
      />

      <Modal
        title="Product Inventory & Issue Details"
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        width={600}
        footer={[
          <Button key="close" onClick={() => setIsViewModalOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedProduct && (
          <div>
            <Descriptions column={1} bordered size="small" style={{ marginTop: 12 }}>
              <Descriptions.Item label="Product Name">
                <strong>{selectedProduct.product_name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Product Code">
                {selectedProduct.product_code || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Store Available Qty">
                <span style={{ fontSize: 16, fontWeight: 700, color: '#52c41a' }}>
                  {selectedProduct.product_qty} {selectedProduct.ut_name || ''}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Total Issued to Employees">
                <span style={{ fontSize: 16, fontWeight: 700, color: '#fa8c16' }}>
                  {selectedProduct.total_issued_qty || 0} {selectedProduct.ut_name || ''}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {selectedProduct.category_name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Brand">
                {selectedProduct.brand_name || 'N/A'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 16 }}>
              <strong>Employee Issued Breakdown:</strong>
              <Table
                style={{ marginTop: 8 }}
                size="small"
                loading={loadingIssued}
                pagination={false}
                dataSource={issuedEmployees}
                rowKey="employee_id"
                columns={[
                  {
                    title: 'Employee Name',
                    dataIndex: 'employee_name',
                    key: 'employee_name',
                  },
                  {
                    title: 'Issued Qty',
                    dataIndex: 'issued_qty',
                    key: 'issued_qty',
                    render: (qty: any) => `${qty} ${selectedProduct.ut_name || ''}`,
                  },
                  {
                    title: 'Issue Date & Time',
                    dataIndex: 'issue_date',
                    key: 'issue_date',
                    render: (date: string) => date || '-',
                  },
                ]}
                locale={{ emptyText: 'No stock currently issued to employees' }}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default InventoryManagements;